import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Mic, Upload, FileUp, CheckCircle2, Trash2, Loader2,
} from 'lucide-react';
import { transcriptsApi, calendarApi } from '../api/services';
import type {
  MeetingTranscript,
  TranscriptExtraction,
  CalendarEvent,
} from '../types/models';
import { transcriptsCopy } from '../copy/transcripts';
import ExtractionCard from '../components/transcripts/ExtractionCard';
import AssignDialog from '../components/transcripts/AssignDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// TranscriptsPage — upload + review meeting transcripts.
// ---------------------------------------------------------------------------

const NONE = '__none__';

const STATUS_TONE: Record<string, { bar: string; chip: string }> = {
  Uploaded: { bar: 'border-l-muted-foreground/40', chip: 'bg-muted text-muted-foreground' },
  Processing: { bar: 'border-l-blue-500', chip: 'bg-blue-500/15 text-blue-700 dark:text-blue-300' },
  Analysed: { bar: 'border-l-emerald-500', chip: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
  Accepted: { bar: 'border-l-emerald-500', chip: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
  Error: { bar: 'border-l-rose-500', chip: 'bg-rose-500/15 text-rose-700 dark:text-rose-400' },
};

function statusTone(status: string) {
  return STATUS_TONE[status] || STATUS_TONE.Uploaded;
}

// ---------------------------------------------------------------------------
// Upload form
// ---------------------------------------------------------------------------

interface UploadFormProps {
  onUploaded: () => Promise<void>;
}

function UploadForm({ onUploaded }: UploadFormProps) {
  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [attendees, setAttendees] = useState('');
  const [speakerLabels, setSpeakerLabels] = useState('');
  const [transcriptText, setTranscriptText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [autoCreateItems, setAutoCreateItems] = useState(false);

  const [batchMode, setBatchMode] = useState(false);
  const [batchFiles, setBatchFiles] = useState<File[]>([]);
  const [batchProgress, setBatchProgress] = useState(0);

  const [matchingEvents, setMatchingEvents] = useState<CalendarEvent[]>([]);
  const [recentEvents, setRecentEvents] = useState<CalendarEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const fmt = (d: Date) => d.toISOString().split('T')[0];
        const events = await calendarApi.getByRange(fmt(yesterday), fmt(today));
        setRecentEvents(events);
      } catch {
        setRecentEvents([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (!meetingDate) {
      setMatchingEvents([]);
      setSelectedEventId('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const events = await calendarApi.getByDate(meetingDate);
        if (!cancelled) {
          setMatchingEvents(events);
          if (events.length > 0 && !title.trim()) {
            setTitle(events[0].title);
            setAttendees(events[0].attendees || '');
            setSelectedEventId(events[0].id);
          }
        }
      } catch {
        if (!cancelled) setMatchingEvents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [meetingDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEventLink = (eventId: string) => {
    setSelectedEventId(eventId);
    const event =
      matchingEvents.find((e) => e.id === eventId) ||
      recentEvents.find((e) => e.id === eventId);
    if (event) {
      setTitle(event.title);
      setAttendees(event.attendees || '');
    }
  };

  const handleBatchUpload = async () => {
    if (batchFiles.length === 0) return;
    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    setBatchProgress(0);

    let successCount = 0;
    let errorCount = 0;
    for (let i = 0; i < batchFiles.length; i++) {
      try {
        const file = batchFiles[i];
        const text = await file.text();
        const fileTitle = file.name.replace(/\.[^.]+$/, '');
        const transcript = await transcriptsApi.upload({
          title: fileTitle,
          meetingDate: new Date().toISOString().split('T')[0],
          transcriptText: text,
        });
        await transcriptsApi.analyse(transcript.id);
        successCount++;
      } catch {
        errorCount++;
      }
      setBatchProgress(Math.round(((i + 1) / batchFiles.length) * 100));
    }

    if (errorCount > 0) {
      setUploadError(`${errorCount} of ${batchFiles.length} files failed to upload.`);
    }
    if (successCount > 0) {
      setUploadSuccess(
        `${successCount} transcript${successCount === 1 ? '' : 's'} uploaded and analysed.`,
      );
    }
    setBatchFiles([]);
    setBatchProgress(0);
    setUploading(false);
    await onUploaded();
  };

  const handleUploadAndAnalyse = async () => {
    if (!title.trim() || !meetingDate || !transcriptText.trim()) return;
    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    try {
      const transcript = await transcriptsApi.upload({
        title: title.trim(),
        meetingDate,
        attendees: attendees.trim() || undefined,
        transcriptText: transcriptText.trim(),
        calendarEventId: selectedEventId || undefined,
      });
      if (autoCreateItems) {
        await transcriptsApi.autoAcceptAsync(transcript.id);
        setUploadSuccess(
          'Transcript uploaded. Analysis is running in the background — extractions will appear when ready (usually within a minute).',
        );
      } else {
        await transcriptsApi.analyse(transcript.id);
        setUploadSuccess('Transcript uploaded and analysed.');
      }
      setTitle('');
      setMeetingDate('');
      setAttendees('');
      setSpeakerLabels('');
      setTranscriptText('');
      setSelectedEventId('');
      await onUploaded();
    } catch (err: any) {
      setUploadError(err.response?.data?.message || err.message || 'Failed to upload transcript.');
    } finally {
      setUploading(false);
    }
  };

  // Build unique event list for the "Link to meeting" Select
  const allEvents = useMemo(() => {
    const seen = new Set<string>();
    const out: CalendarEvent[] = [];
    for (const evt of matchingEvents) {
      if (!seen.has(evt.id)) { seen.add(evt.id); out.push(evt); }
    }
    for (const evt of recentEvents) {
      if (!seen.has(evt.id)) { seen.add(evt.id); out.push(evt); }
    }
    return out;
  }, [matchingEvents, recentEvents]);

  return (
    <Card className="mb-6">
      <CardContent>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">Upload transcript</h2>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={batchMode}
              onCheckedChange={(c) => setBatchMode(!!c)}
              disabled={uploading}
            />
            Batch mode
          </label>
        </div>

        {uploadError && (
          <Alert variant="error" className="mb-3">
            <AlertDescription>{uploadError}</AlertDescription>
          </Alert>
        )}
        {uploadSuccess && (
          <Alert variant="success" className="mb-3">
            <AlertDescription>{uploadSuccess}</AlertDescription>
          </Alert>
        )}

        {batchMode ? (
          <div className="flex flex-col gap-3">
            <Alert variant="info">
              <AlertDescription>
                Select multiple .txt or .md files. Each file name becomes the transcript title and
                today becomes the meeting date.
              </AlertDescription>
            </Alert>
            <div>
              <Button
                variant="outline"
                disabled={uploading}
                onClick={() => document.getElementById('batch-files')?.click()}
                className="gap-1.5"
              >
                <FileUp className="h-4 w-4" />
                {batchFiles.length > 0 ? `${batchFiles.length} file(s) selected` : 'Choose files'}
              </Button>
              <input
                id="batch-files"
                type="file"
                multiple
                accept=".txt,.md"
                hidden
                onChange={(e) => setBatchFiles(Array.from(e.target.files || []))}
              />
            </div>
            {batchFiles.length > 0 && (
              <div className="flex flex-col gap-0.5">
                {batchFiles.map((f, i) => (
                  <p key={i} className="text-sm text-muted-foreground">
                    {f.name} ({(f.size / 1024).toFixed(1)} KB)
                  </p>
                ))}
              </div>
            )}
            {uploading && batchProgress > 0 && (
              <div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-primary transition-all" style={{ width: `${batchProgress}%` }} />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{batchProgress}% complete</p>
              </div>
            )}
            <div className="flex justify-end">
              <Button
                onClick={handleBatchUpload}
                disabled={uploading || batchFiles.length === 0}
                size="lg"
                className="min-w-[200px] gap-1.5"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading
                  ? `Uploading ${batchFiles.length} files…`
                  : `Upload and analyse (${batchFiles.length})`}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="t-title">Title <span className="text-destructive">*</span></Label>
                <Input
                  id="t-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={uploading}
                  placeholder="e.g. Weekly leadership sync"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="t-date">Meeting date <span className="text-destructive">*</span></Label>
                <Input
                  id="t-date"
                  type="date"
                  value={meetingDate}
                  onChange={(e) => setMeetingDate(e.target.value)}
                  disabled={uploading}
                />
              </div>
            </div>

            {allEvents.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="t-event">Link to meeting (optional)</Label>
                <Select
                  value={selectedEventId || NONE}
                  onValueChange={(v) => handleEventLink(v === NONE ? '' : v)}
                  disabled={uploading}
                >
                  <SelectTrigger id="t-event">
                    <SelectValue placeholder="Link to meeting (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Link to meeting (optional)</SelectItem>
                    {allEvents.map((evt) => (
                      <SelectItem key={evt.id} value={evt.id}>
                        {new Date(evt.startTime).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })} — {evt.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="t-attendees">Attendees</Label>
              <Input
                id="t-attendees"
                value={attendees}
                onChange={(e) => setAttendees(e.target.value)}
                disabled={uploading}
                placeholder="e.g. John Smith, Jane Doe, Mark Wilson"
              />
              <p className="text-xs text-muted-foreground">Optional, comma-separated names</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="t-speakers">Speaker labels</Label>
              <Input
                id="t-speakers"
                value={speakerLabels}
                onChange={(e) => setSpeakerLabels(e.target.value)}
                disabled={uploading}
                placeholder="e.g. Speaker 1: John, Speaker 2: Jane"
              />
              <p className="text-xs text-muted-foreground">Optional, helps the AI attribute action items</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="t-text">Transcript text <span className="text-destructive">*</span></Label>
              <Textarea
                id="t-text"
                rows={10}
                value={transcriptText}
                onChange={(e) => setTranscriptText(e.target.value)}
                disabled={uploading}
                placeholder="Paste the full meeting transcript here…"
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={autoCreateItems}
                  onCheckedChange={(c) => setAutoCreateItems(!!c)}
                  disabled={uploading}
                />
                Auto-create work items (skip review)
              </label>
              <Button
                onClick={handleUploadAndAnalyse}
                disabled={uploading || !title.trim() || !meetingDate || !transcriptText.trim()}
                size="lg"
                className="min-w-[200px] gap-1.5"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading
                  ? autoCreateItems ? 'Auto-creating work items…' : 'Analysing transcript…'
                  : autoCreateItems ? 'Upload and auto-create' : 'Upload and analyse'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Transcript card
// ---------------------------------------------------------------------------

interface TranscriptCardProps {
  transcript: MeetingTranscript;
  onChanged: () => Promise<void>;
}

function TranscriptCard({ transcript, onChanged }: TranscriptCardProps) {
  const [tab, setTab] = useState<'extractions' | 'transcript'>('extractions');
  const [showDismissed, setShowDismissed] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [assignTargetId, setAssignTargetId] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await transcriptsApi.delete(transcript.id);
      setDeleteOpen(false);
      await onChanged();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to delete transcript.');
    } finally {
      setDeleting(false);
    }
  };

  const extractions = useMemo(() => transcript.extractions || [], [transcript.extractions]);
  const isAnalysed = transcript.status === 'Analysed' || transcript.status === 'Accepted';
  const tone = statusTone(transcript.status);

  const visible = useMemo(
    () => extractions.filter((e) => showDismissed || !e.wasDismissed),
    [extractions, showDismissed],
  );
  const pending = useMemo(
    () => visible.filter((e) => !e.wasAccepted && !e.wasDismissed),
    [visible],
  );
  const hasPending = pending.length > 0;
  const dismissedCount = extractions.filter((e) => e.wasDismissed).length;

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runAction = async (fn: () => Promise<void>) => {
    setMutating(true);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Action failed.');
    } finally {
      setMutating(false);
    }
  };

  const handleAcceptOne = (ext: TranscriptExtraction) =>
    runAction(async () => {
      await transcriptsApi.accept(transcript.id, [ext.id]);
      setSuccess(`Accepted "${ext.title}" into the plan.`);
      setSelected((s) => { const next = new Set(s); next.delete(ext.id); return next; });
    });

  const handleDismissOne = (ext: TranscriptExtraction) =>
    runAction(async () => {
      await transcriptsApi.dismiss(transcript.id, [ext.id]);
      setSuccess(`Dismissed "${ext.title}".`);
      setSelected((s) => { const next = new Set(s); next.delete(ext.id); return next; });
    });

  const handleAcceptAll = () =>
    runAction(async () => {
      const ids = pending.map((e) => e.id);
      if (ids.length === 0) return;
      await transcriptsApi.accept(transcript.id, ids);
      setSuccess(`Accepted ${ids.length} action item${ids.length === 1 ? '' : 's'} into the plan.`);
      setSelected(new Set());
    });

  const handleAcceptSelected = () =>
    runAction(async () => {
      const ids = Array.from(selected);
      if (ids.length === 0) return;
      await transcriptsApi.accept(transcript.id, ids);
      setSuccess(`Accepted ${ids.length} action item${ids.length === 1 ? '' : 's'} into the plan.`);
      setSelected(new Set());
    });

  const handleAssignConfirm = async (
    targetExecutiveUserId?: string,
    projectId?: string,
  ) => {
    if (!assignTargetId) return;
    await transcriptsApi.assign(transcript.id, {
      extractionIds: [assignTargetId],
      targetExecutiveUserId,
      projectId,
    });
    setSuccess('Action assigned and added to the plan.');
    setAssignTargetId(null);
    setSelected((s) => { const next = new Set(s); next.delete(assignTargetId); return next; });
    await onChanged();
  };

  const assignTarget = assignTargetId
    ? extractions.find((e) => e.id === assignTargetId) ?? null
    : null;

  return (
    <Card className={cn('border-l-[3px]', tone.bar)}>
      <CardContent>
        <div className="mb-3 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <p className="text-base font-semibold">{transcript.title}</p>
              <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold', tone.chip)}>
                {transcript.status}
              </span>
            </div>
            <div className="flex flex-wrap gap-4">
              <p className="text-sm text-muted-foreground">
                {new Date(transcript.meetingDate).toLocaleDateString('en-GB', {
                  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                })}
              </p>
              {transcript.attendees && (
                <p className="text-sm text-muted-foreground">{transcript.attendees}</p>
              )}
              {transcript.tokensUsed != null && (
                <p className="text-xs text-muted-foreground">
                  {transcript.tokensUsed.toLocaleString()} tokens
                </p>
              )}
            </div>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={() => setDeleteOpen(true)}
                aria-label="Delete transcript"
                className="text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete transcript</TooltipContent>
          </Tooltip>
        </div>

        <Dialog open={deleteOpen} onOpenChange={(o) => !o && setDeleteOpen(false)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete transcript?</DialogTitle>
            </DialogHeader>
            <p className="text-sm">
              This will permanently delete <strong>{transcript.title}</strong> and its{' '}
              {extractions.length} extracted item{extractions.length === 1 ? '' : 's'}. Work
              items already created from accepted extractions are not removed.
            </p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleteOpen(false)} disabled={deleting}>
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting}
                className="gap-1.5 bg-rose-600 hover:bg-rose-700"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {error && (
          <Alert variant="error" className="mb-3">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert variant="success" className="mb-3">
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <Tabs value={tab} onValueChange={(v) => setTab(v as 'extractions' | 'transcript')} className="mb-3">
          <TabsList>
            <TabsTrigger value="extractions">
              {transcriptsCopy.tabExtractions}
              {extractions.length > 0 ? ` (${extractions.length})` : ''}
            </TabsTrigger>
            <TabsTrigger value="transcript">{transcriptsCopy.tabTranscript}</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === 'extractions' && (
          <>
            {!isAnalysed ? (
              <p className="py-2 text-sm text-muted-foreground">
                {transcript.status === 'Processing'
                  ? 'Processing — extractions will appear shortly.'
                  : transcript.status === 'Error'
                    ? `Processing failed${transcript.errorMessage ? `: ${transcript.errorMessage}` : '.'}`
                    : 'Awaiting analysis.'}
              </p>
            ) : extractions.length === 0 ? (
              <p className="py-2 text-sm italic text-muted-foreground">
                {transcriptsCopy.noExtractionsFound}
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                    <Checkbox
                      checked={showDismissed}
                      onCheckedChange={(c) => setShowDismissed(!!c)}
                    />
                    {transcriptsCopy.toggleShowDismissed}
                    {dismissedCount > 0 ? ` (${dismissedCount})` : ''}
                  </label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleAcceptSelected}
                      disabled={selected.size === 0 || mutating}
                      className="gap-1.5 text-emerald-600 dark:text-emerald-400"
                    >
                      {mutating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                      {transcriptsCopy.batchAcceptSelected} ({selected.size})
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleAcceptAll}
                      disabled={!hasPending || mutating}
                      className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                    >
                      {mutating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                      {transcriptsCopy.batchAcceptAll} ({pending.length})
                    </Button>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {visible.map((ext) => (
                    <ExtractionCard
                      key={ext.id}
                      extraction={ext}
                      selected={selected.has(ext.id)}
                      onToggleSelect={() => toggleSelect(ext.id)}
                      onAccept={() => handleAcceptOne(ext)}
                      onAssign={() => setAssignTargetId(ext.id)}
                      onDismiss={() => handleDismissOne(ext)}
                      disabled={mutating}
                    />
                  ))}
                </div>
                {!hasPending && !showDismissed && (
                  <div className="py-3 text-center">
                    <p className="text-sm font-semibold">{transcriptsCopy.allHandledHeading}</p>
                    <p className="text-sm text-muted-foreground">{transcriptsCopy.allHandledBody}</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {tab === 'transcript' && (
          <div className="flex flex-col gap-3">
            {transcript.aiSummary && (
              <div className="rounded-2xl border border-blue-500/20 bg-blue-500/[0.06] p-3">
                <p className="mb-1 text-sm font-semibold">AI summary</p>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{transcript.aiSummary}</p>
              </div>
            )}
            <div className="max-h-[480px] overflow-y-auto rounded-2xl border border-border p-3">
              <pre className="whitespace-pre-wrap font-sans text-sm">{transcript.transcriptText}</pre>
            </div>
          </div>
        )}

        <AssignDialog
          open={assignTargetId !== null}
          onClose={() => setAssignTargetId(null)}
          onConfirm={handleAssignConfirm}
          extractionTitle={assignTarget?.title}
        />
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function TranscriptsPage() {
  const [transcripts, setTranscripts] = useState<MeetingTranscript[]>([]);
  const [loading, setLoading] = useState(true);

  const loadTranscripts = useCallback(async () => {
    try {
      const data = await transcriptsApi.getAll();
      setTranscripts(data);
    } catch (err) {
      console.error('Failed to load transcripts', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTranscripts();
  }, [loadTranscripts]);

  useEffect(() => {
    const handler = () => { void loadTranscripts(); };
    window.addEventListener('cadence:transcript-analysed', handler);
    return () => window.removeEventListener('cadence:transcript-analysed', handler);
  }, [loadTranscripts]);

  return (
    <div className="mx-auto max-w-[1200px] p-4 md:p-6">
      <div className="mb-1 flex items-center gap-2">
        <Mic className="h-7 w-7 text-primary" />
        <h1 className="text-2xl font-semibold tracking-tight">Transcripts</h1>
      </div>
      <p className="mb-6 text-sm text-muted-foreground">
        Upload meeting transcripts and review the extracted action items.
      </p>

      <UploadForm onUploaded={loadTranscripts} />

      <h2 className="mb-3 text-xl font-semibold">Past transcripts</h2>

      {loading && (
        <div className="py-8 text-center">
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && transcripts.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Mic className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
            <p className="text-base font-semibold text-muted-foreground">No transcripts yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Upload your first meeting transcript above to get started.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col gap-3">
        {transcripts.map((t) => (
          <TranscriptCard key={t.id} transcript={t} onChanged={loadTranscripts} />
        ))}
      </div>
    </div>
  );
}

export default TranscriptsPage;
